import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { AppShell } from '../shared/ui/AppShell'
import { usePostsDashboardController } from '../features/posts/state/usePostsDashboardController'
import { useIntersectionSentinel } from '../shared/hooks/useIntersectionSentinel'
import { PostsFeedView } from '../features/posts/ui/PostsFeedView'
import { ErrorState, EmptyState } from '../shared/ui/States'
import { PostSkeleton } from '../features/posts/ui/PostSkeleton'
import { TrendDashboard } from '../features/trends/ui/TrendDashboard'
export function HomePage() {
  const { t } = useTranslation()
  const showAnalytics = true
  const controller = usePostsDashboardController()
  const { feed, loadMore, retry } = controller

  const onIntersect = useCallback(() => {
    if (!feed.hasNextPage) return
    if (feed.isFetchingNextPage) return
    if (feed.isLoading) return
    loadMore()
  }, [feed.hasNextPage, feed.isFetchingNextPage, feed.isLoading, loadMore])

  const sentinel = useIntersectionSentinel({
    enabled: feed.hasNextPage,
    onIntersect,
    rootMargin: '800px 0px',
  })

  return (
    <AppShell showAnalytics={showAnalytics} showTopBar>
      <div className="mx-auto w-full max-w-[680px]">
        <div className="px-0 pt-2 pb-2 sm:px-6 sm:pt-8 sm:pb-4">
          <h1 className="m-0 text-lg font-bold text-foreground sm:text-2xl">
            {t('home.title')}
          </h1>
          <p className="mt-1 text-xs text-muted-foreground sm:mt-2 sm:text-[15px]">
            {t('home.description')}
          </p>
        </div>

        <div className="flex flex-col gap-3 p-0 sm:gap-6 sm:p-6 [contain:layout_style_paint]">
          <TrendDashboard />

          {feed.isLoading && feed.posts.length === 0 && (
            <div className="flex flex-col gap-8">
              {[...Array(3)].map((_, i) => (
                <PostSkeleton key={i} />
              ))}
            </div>
          )}

          {Boolean(feed.error) && <ErrorState error={feed.error} onRetry={retry} />}

          {!feed.isLoading && !feed.error && feed.posts.length === 0 && (
            <EmptyState label={t('home.noPosts')} />
          )}

          {feed.posts.length > 0 && (
            <PostsFeedView
              posts={feed.posts}
              hasNextPage={feed.hasNextPage}
              isFetchingNextPage={feed.isFetchingNextPage}
              isLoading={feed.isLoading}
              onLoadMore={loadMore}
              sentinelRef={sentinel.ref}
            />
          )}
        </div>
      </div>
    </AppShell>
  )
}
